import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageBackground from "@/components/PageBackground";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";

type Status = "loading" | "valid" | "already" | "invalid" | "success" | "error";

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState<Status>("loading");

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }

    const validate = async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${token}`,
          { headers: { apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY } }
        );
        const data = await res.json();
        if (res.ok && data.valid) setStatus("valid");
        else if (data.reason === "already_unsubscribed") setStatus("already");
        else setStatus("invalid");
      } catch { setStatus("error"); }
    };
    validate();
  }, [token]);

  const handleUnsubscribe = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("handle-email-unsubscribe", {
        body: { token },
      });
      if (error) throw error;
      if (data?.success) setStatus("success");
      else if (data?.reason === "already_unsubscribed") setStatus("already");
      else setStatus("error");
    } catch { setStatus("error"); }
  };

  const messages: Record<Status, { title: string; desc: string }> = {
    loading: { title: "Laden...", desc: "Bitte warte einen Moment." },
    valid: { title: "E-Mail-Abmeldung", desc: "Möchtest du dich wirklich von unseren E-Mails abmelden?" },
    already: { title: "Bereits abgemeldet", desc: "Du hast dich bereits von unseren E-Mails abgemeldet." },
    invalid: { title: "Ungültiger Link", desc: "Dieser Abmeldelink ist ungültig oder abgelaufen." },
    success: { title: "Erfolgreich abgemeldet", desc: "Du erhältst keine weiteren E-Mails mehr von uns." },
    error: { title: "Fehler", desc: "Es ist ein Fehler aufgetreten. Bitte versuche es später erneut." },
  };

  const { title, desc } = messages[status];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <PageBackground />
      <div className="relative z-10">
      <Header />
      <section className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-lg text-center">
          <h1 className="text-3xl font-bold text-foreground mb-4">{title}</h1>
          <p className="text-foreground/85 mb-8">{desc}</p>
          {status === "valid" && (
            <Button onClick={handleUnsubscribe} size="lg" className="bg-primary hover:bg-primary/80 text-primary-foreground">
              Abmelden bestätigen
            </Button>
          )}
        </div>
      </section>
      <Footer />
      </div>
    </div>
  );
};

export default Unsubscribe;
